import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { clone as cloneSkinned } from 'three/addons/utils/SkeletonUtils.js';
import { CELL, type CellRef, type CityMap } from '../game/map';
import { createSeededRandom } from '../utils/random';
import type { AnimatedModel } from './CharacterModel';

/**
 * Things on the street that aren't him but still cost her time. They arrive
 * gradually: night 1 is just her and him, passers-by from night 2, dogs from night 3.
 * - Dogs asleep by their kennels along her route. Walk past too close (or too
 *   fast) and one wakes up barking: she freezes on the spot for a moment.
 * - Passers-by in a hurry, walking straight at her. If she doesn't
 *   step aside they shoulder into her and she staggers, slowed for a few seconds.
 */

type DogState = 'asleep' | 'growl' | 'barking' | 'awake';

/** Sleeping dogs per night (index = night number). */
const DOGS_BY_NIGHT = [0, 0, 0, 1, 2, 2, 3, 3, 3];
/** First night with passers-by. */
const WALKERS_FROM_NIGHT = 1;

const DOG_FUR = '#c27a42';
const DOG_CREAM = '#f0dcc0';

/**
 * Dog breeds (Quaternius, CC0, via poly.pizza). Each dog picks one at random
 * when a night loads. Heights are standing height in the dog group's
 * (already 1.5x scaled) space.
 */
export const DOG_BREEDS = [
  { file: 'shiba', height: 0.42 },
  { file: 'husky', height: 0.5 },
  { file: 'shepherd', height: 0.54 },
  { file: 'dog', height: 0.46 },
  { file: 'pug', height: 0.3 },
] as const;

/**
 * Which clip plays in each state: the first name a breed actually has wins
 * (the packs name their clips differently; the pug only has Idle and Jump).
 * Sleeping uses the last frame of the death clip, held, with breathing added.
 */
const DOG_CLIPS = {
  sleep: ['Death', 'Idle_2_HeadLow', 'Idle'],
  growl: ['Idle_HitReact_Left', 'HitReact_Left', 'Jump', 'Idle'],
  bark: ['Attack', 'Headbutt', 'Jump', 'Idle'],
  awake: ['Idle'],
} as const;
type DogClipRole = keyof typeof DOG_CLIPS;

type DogTemplate = { root: THREE.Object3D; clips: THREE.AnimationClip[]; roles: Record<DogClipRole, string> };

type DogModel = {
  pivot: THREE.Group;
  mixer: THREE.AnimationMixer;
  actions: Map<string, THREE.AnimationAction>;
  roles: Record<DogClipRole, string>;
  current: THREE.AnimationAction | null;
};

class Dog {
  readonly group = new THREE.Group();
  private readonly body = new THREE.Group();
  private readonly head = new THREE.Group();
  private readonly tail: THREE.Mesh;
  private readonly legs: THREE.Mesh[] = [];
  state: DogState = 'asleep';
  private stateTime = 0;
  private barkTimer = 0;
  private barks = 0;
  seen = false;
  /** A route cell a little before the dog (test hook). */
  approach: CellRef = { c: 0, r: 0 };
  /** The animated breed, once loaded; until then the procedural dog stands in. */
  private model: DogModel | null = null;
  /** Index into DOG_BREEDS, rolled when the night loads. */
  breed = 0;

  constructor(geo: { box: THREE.BoxGeometry; sphere: THREE.SphereGeometry }, mats: Record<string, THREE.Material>) {
    const add = (parent: THREE.Object3D, g: THREE.BufferGeometry, m: THREE.Material, x: number, y: number, z: number, sx: number, sy: number, sz: number) => {
      const mesh = new THREE.Mesh(g, m);
      mesh.position.set(x, y, z);
      mesh.scale.set(sx, sy, sz);
      mesh.castShadow = true;
      parent.add(mesh);
      return mesh;
    };
    // Kennel behind the dog (dog faces -Z, kennel at +Z).
    const kennel = new THREE.Group();
    kennel.position.set(0, 0, 0.75);
    add(kennel, geo.box, mats.wood, 0, 0.32, 0, 0.8, 0.64, 0.7);
    add(kennel, geo.box, mats.dark, 0, 0.26, -0.351, 0.32, 0.4, 0.01);
    const roofL = add(kennel, geo.box, mats.roof, -0.22, 0.75, 0, 0.5, 0.05, 0.8);
    roofL.rotation.z = 0.62;
    const roofR = add(kennel, geo.box, mats.roof, 0.22, 0.75, 0, 0.5, 0.05, 0.8);
    roofR.rotation.z = -0.62;
    this.group.add(kennel);
    // Water bowl.
    add(this.group, geo.sphere, mats.bowl, 0.45, 0.03, -0.1, 0.18, 0.06, 0.18);

    this.group.add(this.body);
    add(this.body, geo.sphere, mats.fur, 0, 0, 0, 0.3, 0.28, 0.6);
    add(this.body, geo.sphere, mats.cream, 0, -0.06, -0.12, 0.22, 0.2, 0.36);
    for (const [x, z] of [[-0.1, -0.2], [0.1, -0.2], [-0.1, 0.2], [0.1, 0.2]]) {
      this.legs.push(add(this.group, geo.box, mats.fur, x, 0.13, z, 0.07, 0.26, 0.07));
    }
    this.head.position.set(0, 0.12, -0.3);
    this.body.add(this.head);
    add(this.head, geo.sphere, mats.fur, 0, 0.06, -0.06, 0.22, 0.2, 0.22);
    add(this.head, geo.sphere, mats.cream, 0, 0.0, -0.17, 0.12, 0.1, 0.16);
    add(this.head, geo.sphere, mats.dark, 0, 0.03, -0.25, 0.04, 0.035, 0.03);
    add(this.head, geo.box, mats.fur, -0.07, 0.18, -0.04, 0.05, 0.09, 0.03).rotation.z = 0.25;
    add(this.head, geo.box, mats.fur, 0.07, 0.18, -0.04, 0.05, 0.09, 0.03).rotation.z = -0.25;
    add(this.head, geo.sphere, mats.eye, -0.055, 0.09, -0.15, 0.02, 0.02, 0.015);
    add(this.head, geo.sphere, mats.eye, 0.055, 0.09, -0.15, 0.02, 0.02, 0.015);
    this.tail = add(this.body, geo.box, mats.fur, 0, 0.14, 0.3, 0.05, 0.05, 0.2);
    this.reset();
  }

  reset(): void {
    this.state = 'asleep';
    this.stateTime = 0;
    this.barks = 0;
    this.seen = false;
    this.pose(0);
    if (this.model) {
      this.model.pivot.rotation.y = 0;
      this.model.pivot.scale.y = 1;
      this.play('sleep', true, 0);
    }
  }

  /** Swap in the animated model (a prepared, facing -Z clone). */
  attachModel(pivotContents: THREE.Object3D, template: DogTemplate): void {
    const pivot = new THREE.Group();
    pivot.add(pivotContents);
    const mixer = new THREE.AnimationMixer(pivotContents);
    const actions = new Map<string, THREE.AnimationAction>();
    for (const clip of template.clips) actions.set(clip.name, mixer.clipAction(clip));
    this.model = { pivot, mixer, actions, roles: template.roles, current: null };
    this.body.visible = false;
    for (const l of this.legs) l.visible = false;
    this.group.add(pivot);
    this.play('sleep', true, 0);
  }

  private play(role: DogClipRole, loop: boolean, fade = 0.25, timeScale = 1): void {
    const m = this.model;
    if (!m) return;
    const name = m.roles[role];
    const next = m.actions.get(name);
    if (!next || next === m.current) return;
    next.reset();
    next.setLoop(loop ? THREE.LoopRepeat : THREE.LoopOnce, Infinity);
    next.clampWhenFinished = !loop;
    next.timeScale = timeScale;
    if (role === 'sleep' && name === 'Death') {
      // Hold the lying-down end pose.
      next.setLoop(THREE.LoopOnce, 1);
      next.clampWhenFinished = true;
      next.time = next.getClip().duration;
    }
    next.play();
    if (m.current && fade > 0) m.current.crossFadeTo(next, fade, false);
    else if (m.current) m.current.stop();
    m.current = next;
  }

  /** 0 = curled up asleep, 1 = standing. */
  private pose(up: number): void {
    this.body.position.y = THREE.MathUtils.lerp(0.16, 0.36, up);
    this.head.rotation.x = THREE.MathUtils.lerp(0.45, -0.15, up);
    for (const l of this.legs) l.visible = up > 0.3;
    this.tail.rotation.x = THREE.MathUtils.lerp(-0.2, 0.7, up);
  }

  /** Trigger radius grows with her pace: tiptoeing past can work. */
  static radiusFor(gait: number): number {
    return gait <= 1 ? 2.8 : gait === 2 ? 3.8 : 5.5;
  }

  /** Returns 'wake' on the frame it starts barking, 'bark' on each bark. */
  update(dt: number, her: THREE.Vector3, gait: number, time: number): 'wake' | 'bark' | null {
    if (this.model) return this.updateModel(dt, her, gait);
    this.stateTime += dt;
    const p = this.group.position;
    const d = Math.hypot(her.x - p.x, her.z - p.z);
    let out: 'wake' | 'bark' | null = null;
    switch (this.state) {
      case 'asleep':
        // Slow breathing.
        this.body.scale.y = 1 + Math.sin(time * 1.6) * 0.03;
        if (d < Dog.radiusFor(gait)) {
          this.state = 'growl';
          this.stateTime = 0;
        }
        break;
      case 'growl':
        this.pose(Math.min(1, this.stateTime / 0.3));
        if (this.stateTime > 0.35) {
          this.state = 'barking';
          this.stateTime = 0;
          this.barkTimer = 0;
          this.barks = 0;
          out = 'wake';
        }
        break;
      case 'barking': {
        this.pose(1);
        this.face(her, dt);
        this.barkTimer -= dt;
        if (this.barkTimer <= 0 && this.barks < 7) {
          this.barkTimer = 0.32 + (this.barks % 3 === 2 ? 0.25 : 0);
          this.barks += 1;
          if (!out) out = 'bark';
        }
        // Lunge on each bark.
        const k = Math.max(0, this.barkTimer - 0.2) / 0.12;
        this.body.position.z = -k * 0.12;
        this.head.rotation.x = -0.15 - k * 0.35;
        this.tail.rotation.y = Math.sin(time * 20) * 0.4;
        if (this.barks >= 7 && this.barkTimer <= 0) {
          this.state = 'awake';
          this.stateTime = 0;
        }
        break;
      }
      case 'awake':
        // Stays up, watching her go.
        this.body.position.z = 0;
        this.face(her, dt);
        this.tail.rotation.y = Math.sin(time * 6) * 0.2;
        break;
    }
    return out;
  }

  /** Same states as the procedural dog, played with the model's own animations. */
  private updateModel(dt: number, her: THREE.Vector3, gait: number): 'wake' | 'bark' | null {
    const m = this.model!;
    this.stateTime += dt;
    m.mixer.update(dt);
    const p = this.group.position;
    const d = Math.hypot(her.x - p.x, her.z - p.z);
    let out: 'wake' | 'bark' | null = null;
    switch (this.state) {
      case 'asleep':
        // Slow breathing.
        m.pivot.scale.y = 1 + Math.sin(this.stateTime * 1.7) * 0.025;
        if (d < Dog.radiusFor(gait)) {
          m.pivot.scale.y = 1;
          this.state = 'growl';
          this.stateTime = 0;
          this.play('growl', false, 0.15);
        }
        break;
      case 'growl':
        this.face(her, dt);
        if (this.stateTime > 0.35) {
          this.state = 'barking';
          this.stateTime = 0;
          this.barkTimer = 0;
          this.barks = 0;
          // Lunging and snapping on the chain, a little faster than the clip.
          this.play('bark', true, 0.15, 1.35);
          out = 'wake';
        }
        break;
      case 'barking':
        this.face(her, dt);
        this.barkTimer -= dt;
        if (this.barkTimer <= 0 && this.barks < 7) {
          this.barkTimer = 0.32 + (this.barks % 3 === 2 ? 0.25 : 0);
          this.barks += 1;
          if (!out) out = 'bark';
        }
        if (this.barks >= 7 && this.barkTimer <= 0) {
          this.state = 'awake';
          this.stateTime = 0;
          this.play('awake', true, 0.4);
        }
        break;
      case 'awake':
        // Stays up, watching her go.
        this.face(her, dt);
        break;
    }
    return out;
  }

  private face(her: THREE.Vector3, dt: number): void {
    const p = this.group.position;
    const want = Math.atan2(-(her.x - p.x), -(her.z - p.z)) - this.group.rotation.y;
    const local = THREE.MathUtils.clamp(Math.atan2(Math.sin(want), Math.cos(want)), -0.9, 0.9);
    const turner = this.model ? this.model.pivot : this.body;
    turner.rotation.y += (local - turner.rotation.y) * Math.min(1, dt * 6);
  }
}

class Passerby {
  readonly group = new THREE.Group();
  active = false;
  bumped = false;
  private heading = 0;
  private locked = false;
  private life = 0;
  private pause = 0;
  private side = 0;

  constructor(readonly model: AnimatedModel) {
    this.group.add(model.root);
    this.group.visible = false;
  }

  get position(): THREE.Vector3 {
    return this.group.position;
  }

  spawn(x: number, z: number, side: number): void {
    this.group.position.set(x, 0, z);
    this.group.visible = true;
    this.active = true;
    this.bumped = false;
    this.locked = false;
    this.life = 0;
    this.pause = 0;
    this.side = side;
    this.model.resetPose();
  }

  despawn(): void {
    this.active = false;
    this.group.visible = false;
  }

  /** Walks at her, head down, not looking. Returns true while still needed. */
  update(dt: number, her: THREE.Vector3, map: CityMap): boolean {
    this.life += dt;
    const p = this.group.position;
    const dx = her.x - p.x;
    const dz = her.z - p.z;
    const d = Math.hypot(dx, dz);
    // Aim at her shoulder until close, then commit to a straight line: she can sidestep.
    if (!this.locked) {
      const len = Math.max(0.001, d);
      const tx = her.x + (-dz / len) * this.side;
      const tz = her.z + (dx / len) * this.side;
      this.heading = Math.atan2(-(tx - p.x), -(tz - p.z));
      if (d < 6.5) this.locked = true;
    }
    let speed = 1.35;
    if (this.pause > 0) {
      this.pause -= dt;
      speed = 0;
    }
    const fx = -Math.sin(this.heading);
    const fz = -Math.cos(this.heading);
    let blocked = false;
    if (speed > 0) {
      const nx = p.x + fx * speed * dt;
      const nz = p.z + fz * speed * dt;
      if (map.isWalkable(nx, nz, 1.2)) p.set(nx, 0, nz);
      else if (map.isWalkable(nx, p.z, 1.2)) p.x = nx;
      else if (map.isWalkable(p.x, nz, 1.2)) p.z = nz;
      else blocked = true;
    }
    this.group.rotation.y = this.heading;
    const m = this.model;
    m.locomotion(blocked ? 0 : speed, 1.4, 3, Infinity);
    m.update(dt);
    m.turnHead(0, 0.25);

    const behind = dx * fx + dz * fz < 0;
    if (this.life > 50 || (behind && d > 30) || (blocked && d > 14)) return false;
    return true;
  }

  /** Shouldered into her: a beat to recover, then off a little to the side. */
  stagger(): void {
    this.bumped = true;
    this.pause = 0.7;
    this.heading += this.side >= 0 ? -0.35 : 0.35;
  }
}

export type HazardEvents = {
  /** A dog woke up this frame (position). */
  wake: THREE.Vector3 | null;
  /** A bark sound this frame (position). */
  bark: THREE.Vector3 | null;
  /** First sleeping dog in view. */
  dogSeen: boolean;
  /** A passer-by bumped her: unit push direction. */
  bump: { x: number; z: number } | null;
};

export type HazardFrame = {
  dt: number;
  her: THREE.Vector3;
  forward: THREE.Vector3;
  speed: number;
  gait: number;
  time: number;
  /** Stranger's position if he's on the street. */
  stranger: THREE.Vector3 | null;
  /** Cells left to home (path distance). */
  homeDist: number;
};

export class Hazards {
  readonly group = new THREE.Group();
  private readonly dogs: Dog[] = [];
  private readonly walkers: Passerby[] = [];
  private readonly geo = { box: new THREE.BoxGeometry(1, 1, 1), sphere: new THREE.SphereGeometry(0.5, 12, 9) };
  private readonly mats: Record<string, THREE.Material>;
  private map: CityMap | null = null;
  private night = 1;
  private rng = createSeededRandom(7);
  private nextWalker = 25;
  private walkerIndex = 0;
  readonly events: HazardEvents = { wake: null, bark: null, dogSeen: false, bump: null };
  enabled = true;
  /** Loaded breeds (by DOG_BREEDS index), scaled and turned to face -Z, ready to clone per dog. */
  private readonly dogTemplates = new Map<number, DogTemplate>();

  constructor() {
    const std = (color: string, roughness = 0.85) => new THREE.MeshStandardMaterial({ color, roughness });
    const eye = new THREE.MeshBasicMaterial({ color: '#ffe9a8' });
    eye.color.multiplyScalar(1.6);
    this.mats = {
      fur: std(DOG_FUR),
      cream: std(DOG_CREAM),
      dark: std('#120f0d', 0.6),
      wood: std('#9a7650'),
      roof: std('#9a3a2c'),
      bowl: std('#2d4f8a', 0.4),
      eye,
    };
  }

  /**
   * Load every dog breed. Each dog swaps over as soon as its breed arrives;
   * until then (or if a file fails) the procedural dog stands in.
   */
  async loadDogModels(baseUrl: string): Promise<void> {
    const results = await Promise.allSettled(DOG_BREEDS.map((b, i) => this.loadBreed(i, `${baseUrl}${b.file}.glb`)));
    const failed = results.filter((r) => r.status === 'rejected');
    if (failed.length === results.length) throw (failed[0] as PromiseRejectedResult).reason;
    for (const r of failed) console.warn('A dog breed failed to load.', (r as PromiseRejectedResult).reason);
  }

  private async loadBreed(index: number, url: string): Promise<void> {
    const gltf = await new GLTFLoader().loadAsync(url);
    const scene = gltf.scene;
    scene.traverse((o) => {
      const mesh = o as THREE.Mesh;
      if (!mesh.isMesh) return;
      mesh.castShadow = true;
      mesh.frustumCulled = false;
      const mats = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      for (const mat of mats) {
        const std = mat as THREE.MeshStandardMaterial;
        if (std.isMeshStandardMaterial) {
          std.roughness = Math.max(std.roughness, 0.8);
          std.metalness = 0;
        }
      }
    });
    // Normalise: standing height, feet on the ground, head toward -Z.
    const wrapper = new THREE.Group();
    wrapper.add(scene);
    // Clip names vary between packs ("AnimalArmature|Idle", "Armature|Idle"...): keep the short name.
    const clips: THREE.AnimationClip[] = [];
    for (const clip of gltf.animations) {
      const short = clip.name.slice(clip.name.lastIndexOf('|') + 1);
      if (clips.some((c) => c.name === short)) continue;
      clip.name = short;
      clips.push(clip);
    }
    const pick = (role: DogClipRole) => DOG_CLIPS[role].find((n) => clips.some((c) => c.name === n)) ?? clips[0]?.name ?? '';
    const roles = { sleep: pick('sleep'), growl: pick('growl'), bark: pick('bark'), awake: pick('awake') };
    const idle = clips.find((a) => a.name === roles.awake);
    if (idle) {
      const mixer = new THREE.AnimationMixer(scene);
      mixer.clipAction(idle).play();
      mixer.update(0);
    }
    scene.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(scene, true);
    const height = Math.max(1e-6, box.max.y - box.min.y);
    scene.scale.multiplyScalar(DOG_BREEDS[index].height / height);
    scene.updateMatrixWorld(true);
    box.setFromObject(scene, true);
    scene.position.y -= box.min.y;
    const head = scene.getObjectByName('Head');
    const centre = box.getCenter(new THREE.Vector3());
    if (head) {
      const hp = head.getWorldPosition(new THREE.Vector3());
      // Rotate so the head points down -Z.
      wrapper.rotation.y = Math.atan2(-(hp.x - centre.x), -(hp.z - centre.z)) * -1;
    }
    scene.position.x -= centre.x;
    scene.position.z -= centre.z;
    this.dogTemplates.set(index, { root: wrapper, clips, roles });
    for (const dog of this.dogs) if (dog.breed === index) this.attachDog(dog);
  }

  private attachDog(dog: Dog): void {
    const template = this.dogTemplates.get(dog.breed);
    if (!template) return;
    dog.attachModel(cloneSkinned(template.root), template);
  }

  /** A loaded body for the passer-by pool. */
  addWalkerModel(model: AnimatedModel): void {
    const w = new Passerby(model);
    this.walkers.push(w);
    this.group.add(w.group);
  }

  /** Lay out this night's dogs along her shortest way home. */
  configure(map: CityMap, homeField: Int16Array, start: CellRef, night: number): void {
    this.map = map;
    this.night = night;
    for (const d of this.dogs) this.group.remove(d.group);
    this.dogs.length = 0;
    const rand = createSeededRandom(1000 + night * 17);

    // Shortest route from the start, following the home distance field downhill.
    const route: CellRef[] = [];
    let cur = { ...start };
    for (let guard = 0; guard < 400; guard += 1) {
      route.push(cur);
      const here = homeField[map.index(cur.c, cur.r)];
      if (here <= 0) break;
      let next: CellRef | null = null;
      for (const [dc, dr] of [[0, -1], [1, 0], [-1, 0], [0, 1]]) {
        const c = cur.c + dc;
        const r = cur.r + dr;
        if (!map.inBounds(c, r)) continue;
        const v = homeField[map.index(c, r)];
        if (v >= 0 && v < here) {
          next = { c, r };
          break;
        }
      }
      if (!next) break;
      cur = next;
    }

    // Plain straight stretches with a wall on at least one side.
    const spots: { cell: CellRef; side: [number, number]; along: number; from: CellRef }[] = [];
    route.forEach((cell, i) => {
      if (i < 4 || i > route.length - 4) return;
      if (map.char(cell.c, cell.r) !== '.') return;
      const ns = map.isPassable(cell.c, cell.r - 1) && map.isPassable(cell.c, cell.r + 1);
      const ew = map.isPassable(cell.c - 1, cell.r) && map.isPassable(cell.c + 1, cell.r);
      if (ns === ew) return;
      const sides: [number, number][] = ns ? [[-1, 0], [1, 0]] : [[0, -1], [0, 1]];
      const walls = sides.filter(([dc, dr]) => map.char(cell.c + dc, cell.r + dr) === '#');
      if (!walls.length) return;
      spots.push({ cell, side: walls[Math.floor(rand() * walls.length)], along: i / route.length, from: route[i - 3] });
    });

    const count = DOGS_BY_NIGHT[Math.min(night, DOGS_BY_NIGHT.length - 1)];
    const picked: typeof spots = [];
    for (let k = 0; k < count && spots.length; k += 1) {
      const want = (k + 0.5 + (rand() - 0.5) * 0.4) / count;
      let best: (typeof spots)[number] | null = null;
      let bestScore = Infinity;
      for (const s of spots) {
        if (picked.some((p) => Math.abs(p.cell.c - s.cell.c) + Math.abs(p.cell.r - s.cell.r) < 6)) continue;
        const score = Math.abs(s.along - want);
        if (score < bestScore) {
          bestScore = score;
          best = s;
        }
      }
      if (best) picked.push(best);
    }
    for (const s of picked) {
      const dog = new Dog(this.geo, this.mats);
      dog.approach = s.from;
      // A different mix of breeds every time the night loads (cosmetic only).
      dog.breed = Math.floor(Math.random() * DOG_BREEDS.length);
      const [dc, dr] = s.side;
      const cx = (s.cell.c + 0.5) * CELL;
      const cz = (s.cell.r + 0.5) * CELL;
      const off = CELL / 2 - 1.7;
      dog.group.position.set(cx + dc * off, 0, cz + dr * off);
      // Face the middle of the street; the kennel sits against the wall.
      dog.group.rotation.y = Math.atan2(dc, dr);
      dog.group.scale.setScalar(1.5);
      this.attachDog(dog);
      map.addBlocker(dog.group.position.x, dog.group.position.z, 0.75);
      this.dogs.push(dog);
      this.group.add(dog.group);
    }
    this.reset();
  }

  reset(): void {
    for (const d of this.dogs) d.reset();
    for (const w of this.walkers) w.despawn();
    this.rng = createSeededRandom(500 + this.night * 31);
    this.nextWalker = 22 + this.rng() * 12;
  }

  /** Gap between passers-by: they get more frequent on later nights. */
  private walkerGap(): number {
    // Night 2: the odd one (~70-95 s apart); by night 8 every 30-50 s.
    const base = Math.max(30, 82 - this.night * 6.5);
    return base + this.rng() * 22;
  }

  get dogCount(): number {
    return this.dogs.length;
  }

  /** Test hook: where the dogs sleep, and whether each is still asleep. */
  dogInfo(): { x: number; z: number; state: string; from: CellRef; breed: string }[] {
    return this.dogs.map((d) => ({ x: d.group.position.x, z: d.group.position.z, state: d.state, from: d.approach, breed: DOG_BREEDS[d.breed].file }));
  }

  /** Test hook: send the next passer-by now. */
  hurryWalker(): void {
    this.nextWalker = 0;
  }

  update(f: HazardFrame): HazardEvents {
    const ev = this.events;
    ev.wake = null;
    ev.bark = null;
    ev.dogSeen = false;
    ev.bump = null;
    const map = this.map;
    if (!map || !this.enabled) return ev;

    for (const dog of this.dogs) {
      const r = dog.update(f.dt, f.her, f.gait, f.time);
      if (r === 'wake') ev.wake = dog.group.position;
      if (r) ev.bark = dog.group.position;
      if (!dog.seen && dog.state === 'asleep') {
        const p = dog.group.position;
        const d = Math.hypot(p.x - f.her.x, p.z - f.her.z);
        const ahead = (p.x - f.her.x) * f.forward.x + (p.z - f.her.z) * f.forward.z > 0;
        if (d < 16 && ahead && map.lineOfSight(f.her.x, f.her.z, p.x, p.z)) {
          dog.seen = true;
          ev.dogSeen = true;
        }
      }
    }

    // Passers-by.
    let anyActive = false;
    for (const w of this.walkers) {
      if (!w.active) continue;
      anyActive = true;
      if (!w.update(f.dt, f.her, map)) {
        w.despawn();
        continue;
      }
      if (!w.bumped) {
        const dx = f.her.x - w.position.x;
        const dz = f.her.z - w.position.z;
        const d = Math.hypot(dx, dz);
        if (d < 0.8) {
          w.stagger();
          ev.bump = { x: dx / Math.max(0.001, d), z: dz / Math.max(0.001, d) };
        }
      }
    }
    if (!anyActive && this.walkers.length && this.night >= WALKERS_FROM_NIGHT) {
      if (f.speed > 0.6) this.nextWalker -= f.dt;
      if (this.nextWalker <= 0) {
        if (f.homeDist > 3 && this.trySpawn(f)) this.nextWalker = this.walkerGap();
        else this.nextWalker = 3;
      }
    }
    return ev;
  }

  /** Someone coming the other way, a little up the street, in plain view. */
  private trySpawn(f: HazardFrame): boolean {
    const map = this.map;
    if (!map) return false;
    for (let d = 26; d >= 16; d -= 2) {
      const x = f.her.x + f.forward.x * d;
      const z = f.her.z + f.forward.z * d;
      if (!map.isWalkable(x, z, 1.4)) continue;
      const cell = map.cellAt(x, z);
      if (map.isShrine(cell.c, cell.r) || 'KruU'.includes(map.char(cell.c, cell.r))) continue;
      if (!map.lineOfSight(f.her.x, f.her.z, x, z)) continue;
      if (f.stranger && Math.hypot(f.stranger.x - x, f.stranger.z - z) < 12) continue;
      const w = this.walkers[this.walkerIndex % this.walkers.length];
      this.walkerIndex += 1;
      const side = (this.rng() - 0.5) * 0.8;
      w.spawn(x, z, side);
      return true;
    }
    return false;
  }

  dispose(): void {
    this.geo.box.dispose();
    this.geo.sphere.dispose();
    for (const m of Object.values(this.mats)) m.dispose();
    for (const w of this.walkers) w.model.dispose();
  }
}
